import Select from '@components/v2/form/Select/Select';
import Switch from '@components/v2/form/Switch/Switch';
import MenuItem from '@components/v2/ui/Menu/MenuItem';
import { Stack } from '@mui/material';
import * as React from 'react';
import LangEnum from '../../../../lang/LangEnum';
import { GeneralSettingsParams } from '../../../../stores/GeneralSettings/generalSettings.types';
import { GeneralSettingsFormTestIds } from '../../../../utils/testIds/GeneralSettingsForm.testIds';
import { isAdminTeceo } from '../../../context/ApplicationContext/applicationContext.utils';
import { useTranslation } from '../../../translation/TranslationContext';
import SettingContainer from '../../SettingContainer';
import { OrderBookBadgeValueType } from '../../enums/orderBadgeDisplayType.enum';
import { handleChangeAndBlurParams } from '../../types/generalSettingsPage.types';
import { getOrderBadgeValueTypeLangEnum } from '../../utils/getOrderBadgeValueTypeSelectLabel.utils';

interface OrderSectionGeneralSettingsProps {
  generalSettings: GeneralSettingsParams;
  handleChangeAndBlur: (params: handleChangeAndBlurParams) => Promise<void>;
  handleBypassOrderOnApprovalAlert: (checked: boolean) => void;
}

const OrderSectionGeneralSettings: React.FC<OrderSectionGeneralSettingsProps> = ({
  generalSettings,
  handleChangeAndBlur,
  handleBypassOrderOnApprovalAlert,
}) => {
  const { translate } = useTranslation();

  const orderBadgeValueTypeItems = Object.keys(OrderBookBadgeValueType).map((type) => ({
    value: type,
    label: translate(getOrderBadgeValueTypeLangEnum(type as OrderBookBadgeValueType)),
  }));

  return (
    <SettingContainer
      title={translate(LangEnum.GLOBAL_ORDERS)}
      description={LangEnum.GENERAL_SETTINGS_ORDERS_DESCRIPTION}
    >
      <Stack sx={{ gap: 2 }}>
        <Select
          label={translate(LangEnum.GENERAL_SETTINGS_ORDER_BADGE_VALUE_TYPE)}
          name="orderBookBadgeValueType"
          value={generalSettings.orderBookBadgeValueType}
          renderValue={(selected) =>
            orderBadgeValueTypeItems.find((item) => item.value === selected)?.label ?? ''
          }
          onChange={(event) => {
            handleChangeAndBlur({
              value: event.target.value as OrderBookBadgeValueType,
              name: event.target.name as keyof GeneralSettingsParams,
            });
          }}
        >
          {orderBadgeValueTypeItems.map((item) => (
            <MenuItem key={item.value} value={item.value}>
              {item.label}
            </MenuItem>
          ))}
        </Select>
        <Switch
          label={translate(LangEnum.GENERAL_SETTINGS_ENABLE_SALE_TYPE)}
          name="enableSaleType"
          size="medium"
          sx={{ ml: 0 }}
          checked={generalSettings.enableSaleType}
          testId="enable_sale_type"
          onChange={(event, checked) =>
            handleChangeAndBlur({
              value: checked,
              name: event.target.name as keyof GeneralSettingsParams,
            })
          }
        />
        <Switch
          label={translate(
            LangEnum.GENERAL_SETTINGS_BYPASS_ORDER_ON_APPROVAL_ON_READY_FOR_DELIVERY,
          )}
          name="bypassOrderOnApprovalOnReadyForDelivery"
          size="medium"
          sx={{ ml: 0 }}
          checked={generalSettings.bypassOrderOnApprovalOnReadyForDelivery}
          testId="bypass_order_on_approval_on_ready_for_delivery"
          onChange={(_, checked) => handleBypassOrderOnApprovalAlert(checked)}
        />
        <Switch
          label={translate(LangEnum.GENERAL_SETTINGS_ALLOW_BLOCKED_CUSTOMER_TO_CREATE_ORDER)}
          name="allowBlockedCustomerToCreateOrder"
          size="medium"
          sx={{ ml: 0 }}
          checked={generalSettings.allowBlockedCustomerToCreateOrder}
          testId={GeneralSettingsFormTestIds.ALLOW_BLOCKED_CUSTOMER_TO_CREATE_ORDER_SWITCH}
          onChange={(_, checked) =>
            handleChangeAndBlur({
              name: 'allowBlockedCustomerToCreateOrder',
              value: checked,
            })
          }
        />
        <Switch
          label={translate(LangEnum.GENERAL_SETTINGS_ALLOW_APPROVE_ORDER_FOR_BLOCKED_CUSTOMER)}
          name="allowApproveOrderForBlockedCustomer"
          size="medium"
          sx={{ ml: 0 }}
          checked={generalSettings.allowApproveOrderForBlockedCustomer}
          testId={GeneralSettingsFormTestIds.ALLOW_APPROVE_ORDER_FOR_BLOCKED_CUSTOMER_SWITCH}
          onChange={(_, checked) =>
            handleChangeAndBlur({
              name: 'allowApproveOrderForBlockedCustomer',
              value: checked,
            })
          }
        />
        <Switch
          label={translate(LangEnum.GENERAL_SETTINGS_SHOW_SHIPPING_COST_LABEL)}
          name="showOrderConditionShippingCost"
          disabled={!isAdminTeceo()}
          size="medium"
          sx={{ ml: 0 }}
          checked={generalSettings.showOrderConditionShippingCost}
          testId="show_order_condition_shipping_cost"
          onChange={(event, checked) =>
            handleChangeAndBlur({
              value: checked,
              name: event.target.name as keyof GeneralSettingsParams,
            })
          }
        />
        <Switch
          label={translate(LangEnum.GENERAL_SETTINGS_BLOCK_CREATE_ORDER_WITHOUT_CUSTOMER)}
          name="blockOrderWithoutCustomer"
          size="medium"
          sx={{ ml: 0 }}
          checked={generalSettings.blockOrderWithoutCustomer}
          testId={GeneralSettingsFormTestIds.BLOCK_CREATE_ORDER_WITHOUT_CUSTOMER_SWITCH}
          onChange={(event, checked) =>
            handleChangeAndBlur({
              value: checked,
              name: event.target.name as keyof GeneralSettingsParams,
            })
          }
        />
      </Stack>
    </SettingContainer>
  );
};

export default OrderSectionGeneralSettings;
